import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useInnerHeroImage } from '../hooks/useInnerHeroImage';
import {
  INNER_HERO_SUBTITLE_CLASS,
  INNER_HERO_TITLE_CLASS,
  InnerHeroBackground,
  InnerHeroShell,
} from '../ui/InnerHeroShell';

interface PageHeroProps {
  tokenPrefix?: string;
  title?: string;
  /** SEO-friendly h2 subtitle rendered above the page title */
  subtitle?: string;
  altText?: string;
  keywords?: string[];
  pageHint?: string;
}

const SLOT_GROUP_DEF: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function PageHero({
  tokenPrefix,
  title = '<p>Page</p>',
  subtitle = '',
  altText = 'Page banner',
  keywords = [],
  pageHint = '',
}: PageHeroProps) {
  const prefix = tokenPrefix || 'pagehero';
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const bgPath = `${prefix}.bg_image`;

  const bg = useInnerHeroImage(bgPath, SLOT_GROUP_DEF, pageHint || prefix, { keywords, altText });

  return (
    <InnerHeroShell
      background={
        <EditableSlotGroup groupPath={bgPath} definition={SLOT_GROUP_DEF} className="absolute inset-0">
          <InnerHeroBackground image={bg.image} alt={bg.alt} />
        </EditableSlotGroup>
      }
    >
      {subtitle ? (
        <RichTextContent
          path={subtitlePath}
          label="Subtitle"
          defaultValue={subtitle}
          as="h2"
          className={INNER_HERO_SUBTITLE_CLASS}
        />
      ) : null}
      <RichTextContent
        path={titlePath}
        label="Title"
        defaultValue={title}
        as="h1"
        className={INNER_HERO_TITLE_CLASS}
      />

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="pagehero.subtitle"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="pagehero.title"
        defaultValue="<p>Page</p>"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="pagehero.bg_image"
        definition={SLOT_GROUP_DEF}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </InnerHeroShell>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageHero',
  displayName: 'Page Hero',
  tokens: {
    'pagehero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '',
    },
    'pagehero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
  },
  slotGroups: {
    'pagehero.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page banner' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(PageHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageHero;
